/**
 * Filters for assigned post-its.
 */
import * as React from 'react';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { cn } from '@/lib/utils';
import {
  POST_IT_ASSIGNED_FILTER_OPTIONS,
  POST_IT_QUARTER_OPTIONS,
  type AssignedFilter,
} from '@/components/post-its/post-it-configs';
import { toggleNumberInList } from '@/components/post-its/post-it-table-utils';

/** Props for {@link PostItsPlanningFilters}. */
interface PostItsPlanningFiltersProps {
  filter: AssignedFilter;
  onFilterChange: (f: AssignedFilter) => void;
  /** Interval in which the post-it period ENDS (deadline) */
  endDateFrom: string;
  endDateTo: string;
  onEndDateFromChange: (date: string) => void;
  onEndDateToChange: (date: string) => void;
  quarterlyPeriods?: number[];
  onQuarterlyPeriodsChange?: (p: number[]) => void;
}

/** Filters for assigned post-its. */
export const PostItsPlanningFilters: React.FC<PostItsPlanningFiltersProps> = ({
  filter,
  onFilterChange,
  endDateFrom,
  endDateTo,
  onEndDateFromChange,
  onEndDateToChange,
  quarterlyPeriods = [],
  onQuarterlyPeriodsChange,
}) => {
  /** Toggle a quarter chip when the assigned filter is quarterly. */
  const togglePeriod = (value: number) => {
    if (!onQuarterlyPeriodsChange) return;
    onQuarterlyPeriodsChange(toggleNumberInList(quarterlyPeriods, value));
  };

  return (
    <div className="filter-groups-container mb-5">
      <div className="filter-group">
        <span className="filter-group-label">Récurrence</span>
        <SegmentedControl
          size="sm"
          value={filter}
          onChange={(v) => onFilterChange(v as AssignedFilter)}
          options={POST_IT_ASSIGNED_FILTER_OPTIONS}
        />
      </div>

      {filter === 'quarterly' && (
        <div className="filter-group">
          <span className="filter-group-label">Période(s)</span>
          <div className="inline-flex rounded-lg overflow-hidden border border-[var(--color-2-nuance-1)]">
            {POST_IT_QUARTER_OPTIONS.map((p, index) => {
              const isActive = quarterlyPeriods.includes(p.value);
              const isLast = index === POST_IT_QUARTER_OPTIONS.length - 1;
              return (
                <Button
                  key={p.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-all border-0',
                    !isLast && 'border-r border-[var(--color-2-nuance-1)]',
                    isActive ? 'session-period-btn--active' : 'session-period-btn',
                  )}
                  onClick={() => togglePeriod(p.value)}
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {filter === 'specific_date' && (
        <div className="filter-group">
          <span className="filter-group-label">Date fin (échéance)</span>
          <div className="w-auto mt-0.5">
            <DateRangeInput
              startDate={endDateFrom || undefined}
              endDate={endDateTo || undefined}
              onStartDateChange={onEndDateFromChange}
              onEndDateChange={onEndDateToChange}
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};
